import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { selectQueryParams } from 'selectors/location'
import { useDrawer } from 'ducks/drawers/hooks'
import { getCurrentDrawerId } from 'ducks/drawers/util'
import { goToPage } from 'ducks/location/actions'
import { INSTALLATION_ACTION_TYPE_LINK } from 'ducks/integrations/utils/installationActionType'
import debug from 'util/debug'

const useIntegrationDrawers = () => {
  const dispatch = useDispatch()
  // workaround to dynamically invoke drawers
  // based on the drawer string type set in integrations.data.js/integrations entity store
  const [currentDrawer, setCurrentDrawer] = useState(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const queryParams = useSelector(selectQueryParams)
  // get the current drawer id from the url string in browser address bar
  const currentDrawerId = getCurrentDrawerId(queryParams)

  const { drawerId, openDrawer: openConfigureDrawer } = useDrawer(
    currentDrawer?.drawerType
  )

  useEffect(
    () => {
      if (isDrawerOpen && currentDrawer && currentDrawerId !== drawerId) {
        // prevent drawer from being triggered to open again if the drawer is closed
        // i.e. closed being, the previous state's drawer is missing/changed from address bar query
        setCurrentDrawer(null)
        setIsDrawerOpen(false)
      } else if (currentDrawer && currentDrawerId !== drawerId) {
        // now useDrawer is initialised with value from currentDrawer
        // invoke openDrawer
        openConfigureDrawer(currentDrawer.drawerResourceId, {})
        setIsDrawerOpen(true)
      }
    },
    [
      currentDrawerId,
      drawerId,
      currentDrawer,
      openConfigureDrawer,
      isDrawerOpen,
    ]
  )

  const handleAccessIntegration = useCallback(
    ({ id, integrationConfig, isInstalled }) => {
      if (!integrationConfig || !id) {
        debug('Must provide id and integrationConfig')
        return false
      }
      const {
        installActionType,
        installActionTypeConfig: {
          linkTo: installLinkTo,
          linkOptions: installLinkOptions,
        } = {},
        configureActionConfig: {
          drawerType: configureDrawerType,
          linkTo: configureLinkTo,
          linkOptions: configureLinkOptions,
        } = {},
      } = integrationConfig
      if (isInstalled && configureDrawerType) {
        // save the drawer that needs to be invoked in state
        // useEffect further up will then trigger the openDrawer
        setCurrentDrawer({
          drawerType: configureDrawerType,
          drawerResourceId: id,
        })
        return false
      }
      if (isInstalled && !configureDrawerType && !!configureLinkTo) {
        dispatch(goToPage(configureLinkTo, configureLinkOptions))
        return false
      }
      if (
        !isInstalled &&
        installActionType === INSTALLATION_ACTION_TYPE_LINK &&
        installLinkTo
      ) {
        dispatch(goToPage(installLinkTo, installLinkOptions))
        return false
      }
      // The click event can't be handled
      return true
    },
    [dispatch]
  )
  return { onAccessIntegration: handleAccessIntegration }
}

export default useIntegrationDrawers
